import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Award, Loader2, Check, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function ModuleQuizDialog({ open, onOpenChange, sessionId, onCertificateIssued }) {
  const [stage, setStage] = useState("intro"); // intro | loading | quiz | result
  const [quiz, setQuiz] = useState(null); // {quiz_id, title, questions}
  const [answers, setAnswers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // {score, passed, certificate_id, results}

  const reset = () => {
    setStage("intro");
    setQuiz(null);
    setAnswers([]);
    setResult(null);
  };

  const handleClose = (o) => {
    if (!o) reset();
    onOpenChange(o);
  };

  const start = async () => {
    if (!sessionId) {
      toast.error("Start a chat first");
      return;
    }
    setStage("loading");
    try {
      const { data } = await api.post(`/ai/sessions/${sessionId}/quiz`);
      setQuiz(data);
      setAnswers(Array(data.questions.length).fill(-1));
      setStage("quiz");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not build the quiz");
      setStage("intro");
    }
  };

  const pick = (qIdx, oIdx) =>
    setAnswers((prev) => prev.map((v, i) => (i === qIdx ? oIdx : v)));

  const submit = async () => {
    if (answers.some((a) => a < 0)) {
      toast.error("Answer every question first");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/ai/quiz/submit", {
        quiz_id: quiz.quiz_id,
        answers,
      });
      setResult(data);
      setStage("result");
      if (data.passed && data.certificate_id) {
        onCertificateIssued?.(data.certificate_id);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="module-quiz-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Award className="h-5 w-5 text-primary" /> Finish module & earn a certificate
          </DialogTitle>
          <DialogDescription>
            Take a quick 5-question check. Score 80% or higher to earn a shareable completion certificate.
          </DialogDescription>
        </DialogHeader>

        {stage === "intro" && (
          <div className="py-4 space-y-4">
            <ul className="text-sm text-muted-foreground space-y-2">
              <li>• The quiz is generated from your conversation so far.</li>
              <li>• Multiple choice, no time limit.</li>
              <li>• You can only take the quiz once per module.</li>
            </ul>
            <Button
              onClick={start}
              className="w-full rounded-full h-11"
              data-testid="module-quiz-start-button"
            >
              Start the quiz
            </Button>
          </div>
        )}

        {stage === "loading" && (
          <div className="py-10 flex flex-col items-center gap-3">
            <Loader2 className="h-7 w-7 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Building your quiz…</p>
          </div>
        )}

        {stage === "quiz" && quiz && (
          <div className="space-y-5" data-testid="module-quiz-questions">
            <h3 className="font-heading text-lg font-semibold">{quiz.title}</h3>
            {quiz.questions.map((q, qi) => (
              <div key={qi} className="rounded-xl border border-border p-4">
                <p className="font-medium mb-3">
                  <span className="text-muted-foreground mr-2">Q{qi + 1}.</span>
                  {q.q}
                </p>
                <div className="space-y-2">
                  {q.options.map((opt, oi) => {
                    const selected = answers[qi] === oi;
                    return (
                      <button
                        key={oi}
                        type="button"
                        onClick={() => pick(qi, oi)}
                        className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition-colors ${
                          selected
                            ? "border-primary bg-primary/5 text-foreground"
                            : "border-border hover:border-primary/40 hover:bg-muted/60"
                        }`}
                        data-testid={`module-quiz-q${qi}-opt${oi}`}
                      >
                        <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full border mr-2 text-xs font-semibold ${
                          selected ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground"
                        }`}>
                          {String.fromCharCode(65 + oi)}
                        </span>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            <Button
              onClick={submit}
              disabled={submitting}
              className="w-full rounded-full h-11"
              data-testid="module-quiz-submit-button"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit answers"}
            </Button>
          </div>
        )}

        {stage === "result" && result && (
          <div className="py-4" data-testid="module-quiz-result">
            <div className={`rounded-2xl p-6 text-center ${
              result.passed
                ? "bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900"
                : "bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900"
            }`}>
              <Award className={`h-10 w-10 mx-auto ${result.passed ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}`} />
              <p className="mt-3 text-sm uppercase tracking-wider font-semibold">
                {result.passed ? "Module complete!" : "Not quite — try again"}
              </p>
              <p className="font-heading text-5xl font-bold mt-1">{result.score}%</p>
              <p className="text-sm text-muted-foreground mt-1">
                {result.correct} of {result.total} correct · pass mark 80%
              </p>
              {result.passed && result.certificate_id && (
                <Button asChild className="mt-5 rounded-full" data-testid="module-quiz-view-cert-button">
                  <Link to={`/certificates/${result.certificate_id}`}>View certificate</Link>
                </Button>
              )}
            </div>

            <div className="mt-5 space-y-3">
              <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">Review</h4>
              {result.results.map((r, i) => (
                <div key={i} className="rounded-xl border border-border p-3 text-sm">
                  <div className="flex items-start gap-2">
                    {r.is_correct ? (
                      <Check className="h-5 w-5 text-emerald-500 shrink-0" />
                    ) : (
                      <X className="h-5 w-5 text-rose-500 shrink-0" />
                    )}
                    <div className="flex-1">
                      <p className="font-medium">{r.q}</p>
                      <p className="text-muted-foreground mt-1">
                        Correct: <span className="font-medium text-foreground">{r.options[r.correct]}</span>
                        {!r.is_correct && r.picked >= 0 && (
                          <>
                            {" · "}You chose: <span className="text-rose-600">{r.options[r.picked]}</span>
                          </>
                        )}
                      </p>
                      {r.explain && <p className="text-xs text-muted-foreground mt-1">{r.explain}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
